import {
  describeGraphicFailure,
  useGraphicSafetyBlockToast,
} from '@/features/graphics/graphic-failure';
import { useResolvedRoutes } from '@/lib/use-routes';
import { apiClient } from '@borradh-workspace/api-client';
import { Link } from '@tanstack/react-router';
import { ImageIcon, Loader2, ShieldAlert } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { useArtifactPanel } from '../artifact-panel-context';
import { ArtifactRow } from '../artifact-row';
import { PendingReRoll } from './pending-reroll';

/** Subset of the `graphic` row the card needs — mirrors GET /graphics/:id. */
interface GraphicStatusResult {
  graphicId: string;
  status: string;
  title?: string;
  errorCode?: string | null;
  errorMessage?: string | null;
  /** When the row last changed: dates a failure for the one-off toast. */
  updatedAt?: string | null;
  outputs?: Array<{
    url: string;
    thumbnailUrl?: string;
    slideOrder?: number;
    status?: 'success' | 'failed';
  }>;
}

interface GraphicStatusCardProps {
  previewSlideIndex?: number;
  /** Tells the conversation how a staged change was answered on the card. */
  onSendMessage?: (text: string) => void;
  /** Initial tool output from `createContent`. */
  data: { graphicId: string; status?: string; title?: string };
  /**
   * The content item this graphic belongs to. Carried into the panel so its
   * Save / Schedule / Reject have something to decide ON — those act on the
   * item, not the file.
   */
  itemId?: string;
  /**
   * Which content_createContent/content_patchContent CALL produced this
   * card. Passed down to `PendingReRoll` so its item-state query reads THIS
   * attempt rather than whatever the item's live cut is — without it, every
   * graphic_status card for the same item (across every past redo in the
   * transcript) reads the identical query-cache entry and reacts together.
   */
  attemptId?: string;
  /**
   * This card is not the newest one for the item — a later
   * content_createContent/content_patchContent card exists further down the
   * transcript (`getStaleGraphicArtifactToolCallIds`). Two things follow:
   * the "selected" highlight is suppressed (only the latest should look
   * open when its artifact is), and so is this card's own `PendingReRoll` —
   * `pendingRegenerate` lives on the ITEM, so every card for it reads the
   * identical proposal, and mounting the "Redo it" / "Leave it" decision on
   * more than one of them offers the same decision twice. Only the latest
   * card owns it. Neither flag hides the card itself — it stays fully
   * visible otherwise.
   */
  suppressSelectedHighlight?: boolean;
  /**
   * The server's own signal (`PatchContentOutput.awaitingApproval`) that this
   * call staged an edit instruction but produced no new asset — the
   * `graphicId` it carries is still whatever was already rendered before
   * this turn. Left unguarded, the card polls that (genuinely finished) old
   * asset and presents it as though THIS instruction had just completed a
   * carousel, when nothing has actually been generated for it yet. Applies to
   * EVERY staged card, including the most recent one — clicking it still
   * opens the panel to approve or reject, it just never claims to be done.
   */
  awaitingApproval?: boolean;
  /**
   * Keep polling the ITEM's live cut (not just this card's own graphic id)
   * even after that graphic reaches a terminal status. Set on a
   * `content_patchContent` card: the edit it staged may land on a NEW
   * graphic id once approved, and without this the card freezes on
   * whatever id it was born with instead of following the re-roll.
   */
  followLatest?: boolean;
  /** For the image picker's service-match ordering, when a redo offers one.
   * Optional — the bare `graphic_status` envelope carries no service context. */
  serviceId?: string | null;
}

const POLL_INTERVAL = 4_000; // 4 seconds — graphics render fast

/**
 * Statuses the card explicitly recognises as in-progress. Anything else that
 * isn't `ready`/`failed` (a future enum value, a malformed payload) is treated
 * as in-progress too — it keeps polling and shows a fallback link instead of
 * a permanent skeleton (audit finding #172).
 */
const KNOWN_IN_PROGRESS = new Set(['rendering', 'draft']);

/**
 * Self-polling result card for `createGraphic`. Shows a skeleton while the
 * worker renders, then swaps to the rendered image(s) once the row flips to
 * `ready`. Mirrors `ProcessingStatus` (videos) but for graphics — graphics
 * have no progress field, so there's no progress bar.
 */
export function GraphicStatusCard({
  previewSlideIndex,
  onSendMessage,
  data: initial,
  itemId,
  attemptId,
  suppressSelectedHighlight,
  awaitingApproval,
  followLatest = false,
  serviceId,
}: GraphicStatusCardProps) {
  const routes = useResolvedRoutes();
  const {
    artifact: openPanelArtifact,
    openArtifact,
    autoOpenArtifact,
  } = useArtifactPanel();
  // An ad open in the panel that is waiting on THIS graphic as its creative.
  const openAdWaitingOnThis =
    openPanelArtifact?.kind === 'ad' &&
    openPanelArtifact.creativeId === initial.graphicId;
  const [data, setData] = useState<GraphicStatusResult>({
    graphicId: initial.graphicId,
    status: initial.status ?? 'rendering',
    title: initial.title,
  });

  // Poll for anything that isn't a terminal status — including unknown values
  // (new enum members, malformed payloads). Previously only rendering/draft
  // polled, so an unknown status froze the card on a permanent spinner.
  const isProcessing = data.status !== 'ready' && data.status !== 'failed';

  const needsOutputs = data.status === 'ready' && data.outputs === undefined;
  const needsFailure =
    data.status === 'failed' &&
    data.errorCode === undefined &&
    data.errorMessage === undefined;

  useEffect(() => {
    const shouldPoll = isProcessing || followLatest || needsOutputs;
    if (!shouldPoll && !needsFailure) return;

    let cancelled = false;
    const refresh = async () => {
      try {
        const state =
          followLatest && itemId
            ? await apiClient.get<{ assetId: string | null }>(
                `content-batches/items/${itemId}/state`
              )
            : null;
        const graphic = await apiClient.get<
          GraphicStatusResult & { id: string }
        >(`graphics/${state?.assetId ?? data.graphicId}`);
        if (cancelled) return;
        if (graphic.id !== initial.graphicId) setRedone(true);
        setData({
          graphicId: graphic.id,
          status: graphic.status,
          title: graphic.title,
          outputs: graphic.outputs,
          errorCode: graphic.errorCode,
          errorMessage: graphic.errorMessage,
          updatedAt: graphic.updatedAt,
        });
      } catch {
        // Silently ignore poll errors — retry next tick.
      }
    };
    void refresh();
    const interval = shouldPoll ? setInterval(refresh, POLL_INTERVAL) : null;

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
  }, [
    isProcessing,
    data.graphicId,
    followLatest,
    itemId,
    needsOutputs,
    needsFailure,
    initial.graphicId,
  ]);

  // Bottom-right toast, once, when this render is blocked by the image
  // provider's safety checks (ENG-1155). The card below says the same thing.
  useGraphicSafetyBlockToast({ ...data, id: data.graphicId });

  const slides = (data.outputs ?? [])
    .filter((o) => o.status !== 'failed')
    .slice()
    .sort((a, b) => (a.slideOrder ?? 0) - (b.slideOrder ?? 0));

  // Open the panel when the render LANDS, once.
  //
  // Same rule as a video: the chat gets a ROW, the panel gets the artwork. A
  // graphic embedded in the transcript is the tallest thing on the page and
  // scrolls away with the turn that made it, and every earlier one stays
  // mounted above the current one.
  //
  // Once, via the ref — reopening a panel the owner deliberately closed is the
  // kind of helpfulness that has to be fought.
  const openedRef = useRef(false);
  // A re-roll fired from the staged branch below lands a BRAND NEW asset in
  // this same card (`onReRolled` keeps updating `data` in place rather than
  // unmounting). `awaitingApproval` is a static prop from the turn that
  // proposed the edit — it never flips back — so once the new render finishes
  // and `data.status` returns to 'ready', the staged branch would still be
  // true and relabel a genuinely finished redo as "not generated yet".
  const [redone, setRedone] = useState(false);
  useEffect(() => {
    // An unrendered edit's "ready" asset is the OLD one, not something this
    // call produced — auto-opening the panel on it would re-surface a render
    // from an earlier turn every time the owner asks for another change.
    if (
      !suppressSelectedHighlight &&
      (!awaitingApproval || redone) &&
      data.status === 'ready' &&
      slides.length > 0 &&
      !openedRef.current
    ) {
      openedRef.current = true;
      // The open AD is waiting on this very graphic, and its panel is already
      // filling in with it. Taking the panel over would swap the ad for its
      // own picture, so the landing is spent quietly instead (once: closing
      // the ad later must not then pop this graphic up). An unrelated open ad
      // does not get in the way.
      if (openAdWaitingOnThis) return;
      autoOpenArtifact({ kind: 'graphic', id: data.graphicId, itemId });
    }
  }, [
    awaitingApproval,
    suppressSelectedHighlight,
    redone,
    data.status,
    data.graphicId,
    slides.length,
    autoOpenArtifact,
    itemId,
    openAdWaitingOnThis,
  ]);

  // Staged, not generated. The instruction only wrote a pending proposal —
  // nothing rendered — so the underlying asset is whatever already existed.
  // Showing it as a finished result here would claim a carousel was made
  // when it wasn't; say plainly that it's waiting on approval instead.
  //
  // This card IS where the decision belongs — it's the newest turn, the one
  // the owner just asked for — so it mounts its own `PendingReRoll` (the SAME
  // confirmation-card shape a fresh create uses) rather than pointing back at
  // an older card. `suppressSelectedHighlight` still gates it: if a LATER
  // edit exists for this same item (two instructions staged before either
  // rendered), this one is no longer the latest either, and showing its own
  // Accept/Reject would offer the same proposal a second time next to the
  // newer card that actually owns it now — in that case only the dashed
  // fallback notice shows, with no interactive footer.
  if (awaitingApproval && !redone && data.status === 'ready') {
    const stagedNotice = (
      <button
        type="button"
        data-testid="claire-card-graphic-status-staged"
        className="w-full rounded-lg border border-dashed bg-card p-3 text-left text-xs text-muted-foreground transition-colors hover:bg-accent/50"
        onClick={() =>
          openArtifact({ kind: 'graphic', id: data.graphicId, itemId })
        }
      >
        <p className="flex items-center gap-1.5 font-medium text-foreground">
          <ImageIcon className="size-3.5" />
          Change staged — not generated yet
        </p>
        <p className="mt-1">Open to approve or reject this change.</p>
      </button>
    );
    return (
      <div className="w-full sm:max-w-sm">
        {itemId && !suppressSelectedHighlight ? (
          <PendingReRoll
            itemId={itemId}
            attemptId={attemptId}
            graphicId={data.graphicId}
            serviceId={serviceId}
            fallback={stagedNotice}
            resolvedWhenNotPending
            onAnswered={onSendMessage}
            onReRolled={(id) => {
              openedRef.current = true;
              setRedone(true);
              setData({
                graphicId: id,
                status: 'rendering',
                title: data.title,
              });
              openArtifact({ kind: 'graphic', id, itemId });
            }}
          />
        ) : (
          stagedNotice
        )}
      </div>
    );
  }

  if (data.status === 'ready') {
    if (slides.length === 0) {
      return (
        <div
          data-testid="claire-card-graphic-status"
          className="w-full rounded-lg border bg-card p-4 text-xs text-muted-foreground sm:max-w-sm"
        >
          Graphic ready — open it in your content library to view.
        </div>
      );
    }

    return (
      <div
        data-testid="claire-card-graphic-status"
        className="w-full sm:max-w-sm"
      >
        <ArtifactRow
          className="w-full"
          artifact={{ kind: 'graphic', id: data.graphicId, itemId }}
          disableSelectedHighlight={suppressSelectedHighlight}
          title={data.title ?? 'Graphic'}
          subtitle={
            slides.length > 1
              ? `Carousel · ${slides.length} slides`
              : 'Graphic · PNG'
          }
          thumbnailUrl={
            (
              slides.find((slide) => slide.slideOrder === previewSlideIndex) ??
              slides[0]
            ).url
          }
        />
        {/* The decision, attached to the post — the graphic's half of what
            Accept does on the clip list editor. Renders nothing unless a
            re-roll is actually waiting. Suppressed on a superseded card —
            see `suppressSelectedHighlight`'s doc comment — so a later
            `content_patchContent` card is the only one offering it. */}
        {itemId && !suppressSelectedHighlight ? (
          <PendingReRoll
            itemId={itemId}
            attemptId={attemptId}
            graphicId={data.graphicId}
            serviceId={serviceId}
            // A re-roll renders into a NEW row, so the card follows it. Left
            // pointing at the id it was born with, the request succeeded and
            // the owner watched the old graphic sit there unchanged.
            onReRolled={(id) => {
              openedRef.current = true;
              setData({
                graphicId: id,
                status: 'rendering',
                title: data.title,
              });
              openArtifact({ kind: 'graphic', id, itemId });
            }}
          />
        ) : null}
      </div>
    );
  }

  if (data.status === 'failed') {
    const failure = describeGraphicFailure(
      data,
      `${data.title ?? 'The graphic'} could not be rendered. Try again, or tweak the request.`
    );
    const FailureIcon = failure.isSafetyBlock ? ShieldAlert : ImageIcon;
    return (
      <div
        data-testid="claire-card-graphic-status"
        data-failure-kind={failure.isSafetyBlock ? 'safety-block' : 'failed'}
        className="w-full rounded-lg border border-destructive/30 bg-card p-4 sm:max-w-sm"
      >
        <div className="flex items-center gap-2 text-sm font-medium text-destructive">
          <FailureIcon className="size-4" />
          {failure.title}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{failure.message}</p>
        {failure.isSafetyBlock && (
          <p className="mt-1 text-xs text-muted-foreground">
            It was not retried automatically. Tell Claire what to change to try
            again.
          </p>
        )}
        {failure.code && (
          <p className="mt-2 text-xs font-medium">
            Error code:{' '}
            <span className="select-all font-mono">{failure.code}</span>
          </p>
        )}
      </div>
    );
  }

  // Unknown status — still polling above, but surface a visible in-progress
  // card with an escape hatch instead of an indefinite skeleton (#172).
  if (!KNOWN_IN_PROGRESS.has(data.status)) {
    return (
      <div
        data-testid="claire-card-graphic-status"
        className="w-full rounded-lg border bg-card p-4 sm:max-w-sm"
      >
        <div className="flex items-center gap-2 text-sm font-medium">
          <Loader2 className="size-4 animate-spin" />
          Working on {data.title ?? 'your graphic'}…
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Taking longer than expected?{' '}
          <Link
            to={routes.contentGallery}
            className="font-medium underline underline-offset-2"
          >
            Open it in Content
          </Link>
        </p>
      </div>
    );
  }

  const processingStatus = (
    <div
      data-testid="claire-card-graphic-status"
      role="status"
      className="flex items-center gap-2 py-2 text-xs text-muted-foreground"
    >
      <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
      Creating graphic…
    </div>
  );

  // A persisted card can still carry the stale `rendering` status from the
  // turn that created it even though the item already has a staged re-roll.
  // Read the item decision in parallel with the graphic poll so a slow or
  // failed graphic refresh cannot leave the owner with an ordinary composer
  // and no Accept / Reject controls. `PendingReRoll` renders the status as its
  // fallback when there is no proposal, so genuinely rendering graphics keep
  // the same compact treatment.
  if (itemId && !suppressSelectedHighlight) {
    return (
      <PendingReRoll
        itemId={itemId}
        attemptId={attemptId}
        graphicId={data.graphicId}
        serviceId={serviceId}
        fallback={processingStatus}
        onReRolled={(id) => {
          openedRef.current = true;
          setRedone(true);
          setData({
            graphicId: id,
            status: 'rendering',
            title: data.title,
          });
          openArtifact({ kind: 'graphic', id, itemId });
        }}
      />
    );
  }

  return processingStatus;
}
